import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error(lang === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error(lang === "es" ? "Mínimo 8 caracteres" : "Minimum 8 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(lang === "es" ? "Contraseña actualizada" : "Password updated");
      await refresh();
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 grid place-items-center px-4" data-testid="change-password-page">
      <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900/40 p-8">
        <div className="w-10 h-10 rounded-md bg-blue-500/10 text-blue-400 grid place-items-center">
          <KeyRound className="w-5 h-5" />
        </div>
        <h1 className="mt-4 font-display text-2xl font-semibold text-zinc-100 tracking-tight">
          {lang === "es" ? "Cambia tu contraseña" : "Change your password"}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {lang === "es"
            ? `Tu contraseña temporal debe reemplazarse antes de continuar. Cuenta: ${user?.email || ""}`
            : `Your temporary password must be replaced before you continue. Account: ${user?.email || ""}`}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="overline block mb-1.5">{lang === "es" ? "Contraseña actual" : "Current password"}</span>
            <input data-testid="cp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full h-10 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="overline block mb-1.5">{lang === "es" ? "Nueva contraseña" : "New password"}</span>
            <input data-testid="cp-new" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="w-full h-10 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="overline block mb-1.5">{lang === "es" ? "Confirmar nueva contraseña" : "Confirm new password"}</span>
            <input data-testid="cp-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full h-10 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm text-zinc-100" />
          </label>
          <button data-testid="cp-submit" type="submit" disabled={loading} className="btn-scale w-full h-10 rounded-md bg-blue-500 text-white font-medium hover:bg-blue-400 flex items-center justify-center gap-2 disabled:opacity-70">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {lang === "es" ? "Guardar" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
